import {NoOperation} from '@/library/utils';
import {computed, onMounted, type Ref, ref, useAttrs, watch} from 'vue';

export function useFormSelectUser(
    select: Ref<Optional<IFormSelectContext>>,
    modelValue: Ref<Optional<FormUserSelectValue>>,
    inputLocked: Ref<Optional<boolean>>,
    focus: Ref<Optional<boolean>>,
    removeCallbackPromise: Ref<Optional<(person: User) => Promise<any>>>,
    useEmptyValue: Ref<Optional<boolean>>,
    emitCallback: {
        (eventName: 'update:modelValue', input: FormUserSelectValue): void;
    },
    type: 'user' | 'employee' | 'entity' | 'role' = 'user',
)
{
    const attrs = useAttrs();

    const selection = ref<FormUserSelectValue>(null);
    const persons   = ref<number[]>([]);

    const personBoxAriaLabel = computed(() =>
    {
        let typeTerm = '';
        let multiple = attrs.multiple;

        if (type === 'user')
        {
            typeTerm = 'Nutzer';
        }
        else if (type === 'employee')
        {
            typeTerm = 'Mitarbeiter';
        }
        else if (type === 'entity')
        {
            typeTerm = 'Abteilung' + (multiple ? 'en' : '');
        }
        else if (type === 'role')
        {
            typeTerm = 'Rolle' + (multiple ? 'n' : '');
        }

        return 'Auswahl' + (typeTerm ? ' ' + typeTerm : '');
    });

    const personBoxType = computed(() =>
    {
        return 'person-box-' + type;
    });

    onMounted(() =>
    {
        if (focus.value)
        {
            select.value!.open();
        }
    });

    const updatePersons = (newValue: FormUserSelectValue) =>
    {
        if (!newValue)
        {
            persons.value = [];
        }
        else if (Array.isArray(newValue))
        {
            persons.value = newValue.filter((v) => v);
        }
        else
        {
            persons.value = newValue ? [newValue] : [];
        }
    };

    const removePerson = (person: User) =>
    {
        if (Array.isArray(selection.value))
        {
            selection.value.splice(selection.value.indexOf(person.id), 1);
        }
        else
        {
            selection.value = null;
        }

        emitCallback('update:modelValue', selection.value);
    };

    const getFormElement = () =>
    {
        return select.value;
    };

    selection.value = modelValue.value || useEmptyValue.value ? modelValue.value : selection.value;

    updatePersons(selection.value);

    const remove = (person: User) =>
    {
        if (inputLocked.value)
        {
            return;
        }

        if (removeCallbackPromise.value)
        {
            removeCallbackPromise.value(person)
            .then(() => removePerson(person))
            .catch(NoOperation);
        }
        else
        {
            removePerson(person);
        }
    };

    watch(modelValue, (newValue: FormUserSelectValue) =>
    {
        selection.value = newValue;
    });

    watch(selection, (newValue: FormUserSelectValue) =>
    {
        updatePersons(newValue);

        emitCallback('update:modelValue', newValue);
    }, {deep: true});

    const returnValues = {
        type,
        selection,
        persons,
        personBoxAriaLabel,
        personBoxType,
        updatePersons,
        removePerson,
        getFormElement,
        remove,
    };

    return {
        expose: returnValues,
        ...returnValues,
    };
}